import { useAction, useAtom } from '@reatom/npm-react'
import BigNumber from 'bignumber.js'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import {
  PaletteIcon,
  ShortcutsIcon,
  SoundIcon,
  ScaleIcon,
  useHapticFeedback,
} from '@blade/ui-kit'
import { Switch } from '@blade/ui-kit'
import { skipUnsafeUrlWarningAtom } from '@root/entities/deeplinking/unsafe-url-dialog'
import { isFlashTemporalDistinctEnabledAtom } from '@root/entities/pair/atoms/reatom-flash-temporal-distinct'
import { showPairActivityNotificationsAtom } from '@root/features/pair-activity/atoms'
import { showShortcutsHintsAtom } from '@root/features/shortcut/atoms/show-shortcuts-hints'
import { skipShowAuditAtom } from '@root/features/trade/trading'
import {
  pushNotificationsEnabledAtom,
  pushSoundNotificationsActions,
  soundNotificationsEnabledAtom,
  toggleSoundNotificationsActions,
} from '@root/features/wallet-tracker/atoms/reatom-alerts-notifications'
import {
  isTauriAtom,
  isTauriOrBrowserLocalAtom,
  PREDEFINED_SCALE_FACTORS,
  scaleFactorAtom,
} from '@root/shared'
import {
  NotificationBellIcon,
  SegmentControl,
  WarningTriangleIcon,
  EyeIcon,
  Crown2Icon,
} from '@root/shared/ui-kit'

import { settingScreenAction } from '../atoms'

import { SettingsItem } from './settings-item'

type Props = {}

const toDisplayString = (scaleFactor: number) =>
  BigNumber(scaleFactor).multipliedBy(100).toString()

export const SettingsAppSettings: React.FC<Props> = () => {
  const { t } = useTranslation()
  const setSettingScreen = useAction(settingScreenAction)
  const [isTauri] = useAtom(isTauriAtom)
  const [isTauriOrBrowserLocal] = useAtom(isTauriOrBrowserLocalAtom)
  const [scaleFactor] = useAtom(scaleFactorAtom)
  const setScaleFactor = useAction(scaleFactorAtom.setScaleFactorAction)
  const [skipShowAudit] = useAtom(skipShowAuditAtom)
  const toggleSkipShowAudit = useAction(skipShowAuditAtom.toggle)

  const [soundNotificationsEnabled] = useAtom(soundNotificationsEnabledAtom)
  const toggleSoundNotifications = useAction(toggleSoundNotificationsActions)
  const [pushNotificationsEnabled] = useAtom(pushNotificationsEnabledAtom)
  const togglePushNotifications = useAction(pushSoundNotificationsActions)
  const [showPairActivityNotifications] = useAtom(
    showPairActivityNotificationsAtom,
  )
  const toggleShowPairActivityNotifications = useAction(
    showPairActivityNotificationsAtom.toggle,
  )

  const [skipUnsafeUrlWarning] = useAtom(skipUnsafeUrlWarningAtom)
  const toggleSkipUnsafeUrlWarning = useAction(skipUnsafeUrlWarningAtom.toggle)

  const [isFlashDistinctEnabled] = useAtom(isFlashTemporalDistinctEnabledAtom)
  const toggleIsFlashDistinctEnabled = useAction(
    isFlashTemporalDistinctEnabledAtom.toggle,
  )

  const [showShortcutsHints] = useAtom(showShortcutsHintsAtom)
  const toggleShowShortcutsHints = useAction(showShortcutsHintsAtom.toggle)

  const haptics = useHapticFeedback()
  const currentScaleFactor = scaleFactor?.value ?? 1

  return (
    <Root>
      <Items>
        {isTauri ? (
          <SettingsItem
            disabled={false}
            icon={<ScaleIcon size={18} />}
            title={t('settings.app-settings.interface-size')}
            endContent={
              <SegmentControl
                getId={(x) => x.toString()}
                value={currentScaleFactor}
                onChange={(x) => {
                  haptics.impactOccurred('light')
                  setScaleFactor(x, 'user')
                }}
              >
                {PREDEFINED_SCALE_FACTORS.map((scaleFactor, i) => (
                  <SegmentControl.Item key={i} value={scaleFactor}>
                    {toDisplayString(scaleFactor)}%
                  </SegmentControl.Item>
                ))}
              </SegmentControl>
            }
          />
        ) : undefined}
        <SettingsItem
          disabled={false}
          icon={<WarningTriangleIcon size={18} />}
          title={t(
            'settings.app-settings.show-social-network-safety-link-alert',
          )}
          endContent={
            <Switch
              checked={!skipUnsafeUrlWarning}
              onCheckedChange={() => {
                haptics.impactOccurred('light')
                toggleSkipUnsafeUrlWarning()
              }}
            />
          }
        />
        <SettingsItem
          disabled={false}
          icon={<WarningTriangleIcon size={18} />}
          title={t('settings.app-settings.show-pair-audit-alert')}
          endContent={
            <Switch
              checked={!skipShowAudit}
              onCheckedChange={() => {
                haptics.impactOccurred('light')
                toggleSkipShowAudit()
              }}
            />
          }
        />
        <SettingsItem
          disabled={false}
          icon={<SoundIcon size={18} />}
          title={t('settings.app-settings.use-sound-notifications')}
          endContent={
            <Switch
              checked={soundNotificationsEnabled}
              onCheckedChange={() => {
                haptics.impactOccurred('light')
                toggleSoundNotifications()
              }}
            />
          }
        />
        {isTauriOrBrowserLocal ? (
          <SettingsItem
            disabled={false}
            icon={<NotificationBellIcon size={18} />}
            title={t('settings.app-settings.show-system-notifications')}
            endContent={
              <Switch
                checked={pushNotificationsEnabled}
                onCheckedChange={() => {
                  haptics.impactOccurred('light')
                  togglePushNotifications()
                }}
              />
            }
          />
        ) : undefined}
        <SettingsItem
          disabled={false}
          icon={<Crown2Icon size={18} />}
          title={t('settings.app-settings.show-pair-activity-notifications')}
          endContent={
            <Switch
              checked={showPairActivityNotifications}
              onCheckedChange={() => {
                haptics.impactOccurred('light')
                toggleShowPairActivityNotifications()
              }}
            />
          }
        />
        <SettingsItem
          disabled={false}
          icon={<EyeIcon size={18} />}
          title={t('settings.app-settings.is-flash-distinct-enabled')}
          endContent={
            <Switch
              checked={isFlashDistinctEnabled}
              onCheckedChange={() => {
                haptics.impactOccurred('light')
                toggleIsFlashDistinctEnabled()
              }}
            />
          }
        />
        <SettingsItem
          disabled={false}
          icon={<ShortcutsIcon size={18} />}
          title={t('settings.app-settings.show-shortcuts-hints')}
          endContent={
            <Switch
              checked={showShortcutsHints}
              onCheckedChange={() => {
                haptics.impactOccurred('light')
                toggleShowShortcutsHints()
              }}
            />
          }
        />
        <SettingsItem
          icon={<PaletteIcon size={18} />}
          title={t('settings.app-settings.theme')}
          onClick={() => {
            setSettingScreen('app-settings-theme')
          }}
        />
      </Items>
    </Root>
  )
}

const Root = styled.div``

const Items = styled.div`
  padding: 14px;
  gap: 10px;

  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
`
